import {
  ACCOUNT_MODAL_CONFIG_TOKEN,
  CHECKING_ACCOUNT_DROPDOWN_TOKEN,
  DOWNLOAD_BUTTON_CONFIG,
  DOWNLOAD_CONFIG,
  FINANCIAL_RECORDS_LIST_COMPONENT,
  provideBfmDownloads,
} from '@celerofinancas/common-domain';
import {
  formatCash,
  formatMoney,
  getMonthName,
  PaymentsMethodsOptions,
} from '@celerofinancas/core-ts';
import { provideCdsSnackbar } from '@celerofinancas/design-system';
import {
  COMPANY_IDENTITY_CONFIGURATION_TOKEN,
  type CompanyIdentity,
} from '@celerofinancas/pcp-client-ui';
import { QUERY_PARAMS_CONFIG } from '@celerofinancas/service-utils';
import {
  BfmFinancialRecordsListComponent,
  CASH_FLOW_MODAL_TOKEN,
  CHECKING_ACCOUNTS_DISPLAY_TYPE,
  FINANCIAL_RECORD_DETAIL_MODAL_COMPONENT,
  FINANCIAL_RECORD_DETAILS_DOCUMENTS_CONFIGS,
  FINANCIAL_RECORD_STATUS_TOKEN,
} from '@celerofinancas/ui-cash-flow';
import { DOWNLOAD_STATUS_CARD_CONFIG } from '@celerofinancas/ui-common';
import {
  SOURCE_IMAGE_DIRECTIVE_CONFIG,
  TOOLTIP_COMPONENT_TOKEN,
} from '@celerofinancas/ui-directives';
import { ENVIRONMENTS_CONFIGURATION, HELP_CENTER_CONFIGURATION } from '@celerofinancas/ui-environments';
import { MODAL_COMPONENT_EXTERNAL } from '@celerofinancas/ui-modals';
import { OAUTH_STORAGE_NAMES } from '@celerofinancas/ui-oauth';
import { FORMAT_CASH_TOKEN, MONEY_PIPE_TOKEN } from '@celerofinancas/ui-pipes';
import {
  type BarGraphTooltipItem,
  COMPARATIVE_REPORT_FILTER_CONFIG,
  COMPARATIVE_REPORT_HEADER_CONFIG,
  COMPARATIVE_REPORT_LIST_CONFIG,
  COMPARATIVE_REPORT_TABLE_CONFIG,
  FORMAT_BAR_TOOLTIP_VALUE_TOKEN,
  FORMAT_CASH_AND_MONEY_TOKEN,
  FORMAT_DATE_TICK,
} from '@celerofinancas/ui-reports';
import { CompanyState } from '@celerofinancas/state-management';
import { environment } from '@environments/environment';
import { CreditCardsHttpDataSourceService } from '@pages/credit-cards/credit-cards-data-source.service';
import { CREDIT_CARDS_TABLE_DATA_SOURCE } from '@pages/credit-cards/credit-cards-data-source.token';
import { BfmFinancialRecordDetailsModalWrapperComponent } from '@pages/statement/modal-wrapper/modal-wrapper.component';
import { ModalBaseComponent } from '@shared/components/modal-base/modal-base.component';
import { TooltipComponent } from '@shared/components/tooltip/tooltip.component';
import { TRANSACTION_TYPES } from '@shared/constants/transaction-types';
import { Status } from '@shared/models/status';
import { Store } from '@ngxs/store';
import { CAIXA_ACCOUNT_MODAL_CONFIG } from './account-modal.config';
import { LIST_HEADER_TOKENS } from './list-header.tokens';
import { DOWNLOAD_CONFIRM_MODAL_CONFIG, DOWNLOAD_CONFIRM_MODAL_CONFIG_DEFAULT, DownloadConfirmModalConfig } from '@celerofinancas/ui-common';

/**
 * Formats a "Resultado mensal" bar tooltip value the way Caixa's statements
 * do: the sign is never rendered, it is carried by a trailing C (credit) /
 * D (debit) indicator.
 *
 * The graph plots magnitudes, so an outflow reaches here as a positive number
 * and the `money` pipe would label it C. The series' direction is what
 * restores the sign, so "Saídas previstas" reads D (card 86e2xjhkq).
 *
 * Zero carries no indicator, matching what `MoneyPipe` already renders for it.
 *
 * Deliberately does not reuse the `FORMAT_CASH_AND_MONEY_TOKEN` factory below:
 * that one formats the "Saldo final de cada dia" axis and is out of this
 * card's scope, so the two are kept independent on purpose.
 *
 * @param item Hovered bar, carrying its plotted value and cash direction
 * @returns Formatted value, e.g. `R$ 595,00 D`
 * @example
 * formatBarTooltipValue({ value: 595, direction: 'expense' }); // 'R$ 595,00 D'
 * formatBarTooltipValue({ value: 595, direction: 'revenue' }); // 'R$ 595,00 C'
 * formatBarTooltipValue({ value: 0, direction: 'expense' });   // 'R$ 0,00'
 */
export function formatBarTooltipValue({ value, direction }: BarGraphTooltipItem): string {
  const signedValue = direction === 'expense' ? -Math.abs(value) : value;

  if (!signedValue) {
    return formatMoney(0);
  }

  // `showNegative: false` mirrors MONEY_PIPE_TOKEN, so the minus sign is not
  // rendered on top of the D indicator.
  const money = formatMoney(signedValue, 'R$', '.', ',', false);
  const indicator = signedValue < 0 ? TRANSACTION_TYPES.DEBIT : TRANSACTION_TYPES.CREDIT;

  return `${money} ${indicator}`;
}

/**
 * Application tokens for dependency injection.
 */
export const APPLICATION_TOKENS = [
  {
    provide: COMPANY_IDENTITY_CONFIGURATION_TOKEN,
    useFactory: (store: Store): CompanyIdentity => ({
      get: () => store.selectSnapshot(CompanyState.getActiveCompanyPk) ?? '',
      set: (_id: string) => {},
    }),
    deps: [Store],
  },
  {
    provide: ENVIRONMENTS_CONFIGURATION,
    useValue: environment,
  },
  {
    provide: CREDIT_CARDS_TABLE_DATA_SOURCE,
    useExisting: CreditCardsHttpDataSourceService,
  },
  {
    provide: DOWNLOAD_CONFIG,
    useValue: {
      dictionary: {
        date_type: {
          label: 'Tipo de data',
          values: {
            payment: 'Pagamento',
            due: 'Vencimento',
            scheduled_date: 'Agendar para',
            creation: 'Criação',
            emission: 'Emissão',
            accrual_basis: 'Competência',
            update: 'Atualização',
          },
        },
        date_range_start: { label: 'Data início', format: 'date' },
        date_range_end: { label: 'Data fim', format: 'date' },
        accrual_basis_month: { label: 'Mês de competência' },
        accrual_basis_year: { label: 'Ano de competência' },
        financial_record_type: {
          label: 'Tipo de registro',
          values: { revenue: 'Entradas', expense: 'Saídas' },
        },
        start_value: { label: 'Valor mínimo', format: 'money' },
        end_value: { label: 'Valor máximo', format: 'money' },
        payment_methods: {
          label: 'Método de pagamento',
          values: Object.fromEntries(PaymentsMethodsOptions),
        },
        is_done: { label: 'Status do registro', values: { true: 'Realizado' } },
        is_predicted: { label: 'Status do registro', values: { true: 'Previsto' } },
        is_late: { label: 'Status do registro', values: { true: 'Atrasado' } },
        is_scheduled: { label: 'Status do registro', values: { true: 'Agendado' } },
        bank_id: { label: 'Banco', format: 'bank' },
      },
    },
  },
  { provide: HELP_CENTER_CONFIGURATION, useValue: {} },
  { provide: TOOLTIP_COMPONENT_TOKEN, useValue: TooltipComponent },
  { provide: MODAL_COMPONENT_EXTERNAL, useValue: ModalBaseComponent },
  { provide: FINANCIAL_RECORDS_LIST_COMPONENT, useValue: BfmFinancialRecordsListComponent },
  {
    provide: SOURCE_IMAGE_DIRECTIVE_CONFIG,
    useValue: {
      useThemeOnPath: true,
      usePaletteOnPath: false,
      assetsUrl: environment.cdnUrl,
    },
  },
  {
    provide: FINANCIAL_RECORD_DETAIL_MODAL_COMPONENT,
    useValue: {
      headerTitle: 'Detalhe do registro',
      component: BfmFinancialRecordDetailsModalWrapperComponent,
    },
  },
  {
    provide: FORMAT_CASH_TOKEN,
    useValue: { indicatorPosition: 'after', showNegativeIndicator: false, space: '' },
  },
  {
    provide: MONEY_PIPE_TOKEN,
    useValue: {
      prefix: 'R$',
      thousand: '.',
      decimalSeparator: ',',
      showNegative: false,
      displayThousands: true,
      showIndicator: true,
    },
  },
  {
    provide: COMPARATIVE_REPORT_HEADER_CONFIG,
    useValue: {
      showTitle: false,
      showButtonDownloadCSV: false,
      textTooltipHideTransientMovements:
        'Movimentações transitórias são: transferência entre contas; transações internas; resgates; aplicações automáticas e estornos de pagamentos ou recebimentos.',
    },
  },
  {
    provide: COMPARATIVE_REPORT_FILTER_CONFIG,
    useValue: {
      periodLabel: 'Período de Análise',
      buttonGenerateLabel: 'Analisar',
      showIconCostCenter: true,
      textIconInfoCostCenter:
        'Você pode criar um Centro de custos para organizar e acompanhar entradas e saídas de dinheiro da empresa por área, projeto ou finalidade.',
    },
  },
  {
    provide: COMPARATIVE_REPORT_LIST_CONFIG,
    useValue: {
      noContentDescriptionsTitle: 'Selecione um período para gerar o relatório',
      noContentDescriptionsSubtitle:
        'Para ter a análise do comparativo de resultados, defina os meses nos campos acima e clique em <b>Analisar</b>.',
      indicators: [
        { class: 'positive', text: 'Receitas' },
        {
          class: 'negative',
          text: 'Redutores',
          percent: 'Porcentagem sobre as receitas',
        },
        {
          text: 'Receita Líquida',
          tip: 'Valor de receita que sobra depois de tirar os redutores das vendas.',
          percent: 'Porcentagem sobre as receitas',
        },
        {
          class: 'negative',
          text: 'Custos',
          percent: 'Porcentagem sobre as receitas',
        },
        {
          text: 'Margem de Contribuição',
          tip: 'Valor que sobra após o pagamento de todos os custos.',
          percent: 'Porcentagem sobre as receitas',
        },
        {
          class: 'negative',
          text: 'Despesas Operacionais',
          percent: 'Porcentagem sobre as receitas',
        },
        {
          text: 'Resultado Operacional',
          tip: 'Valor de lucro ou prejuízo vindo das atividades da empresa.',
          percent: 'Porcentagem sobre as receitas',
        },
        {
          class: 'negative',
          text: 'Impostos',
          percent: 'Porcentagem sobre as receitas',
        },
        {
          text: 'Resultado Após Impostos',
          tip: 'Resultado do valor da operação após pagamento dos impostos.',
          percent: 'Porcentagem sobre as receitas',
        },
        { class: 'positive', text: 'Entradas Não Operacionais' },
        { class: 'negative', text: 'Saídas Não Operacionais' },
        {
          text: 'Resultado Líquido',
          tip: 'Resultado do valor total de lucro da empresa no mês.',
          percent: 'Porcentagem sobre as receitas',
        },
        { class: 'sum', text: 'Saldo Inicial agrupado' },
        { class: 'sum', text: 'Saldo Final agrupado' },
      ],
    },
  },
  {
    provide: COMPARATIVE_REPORT_TABLE_CONFIG,
    useValue: {
      useDirectiveColor: true,
      showCustomLine: true,
      usePipeMoney: true,
    },
  },
  {
    provide: QUERY_PARAMS_CONFIG,
    useValue: false,
  },
  {
    provide: FINANCIAL_RECORD_STATUS_TOKEN,
    useClass: Status,
  },
  {
    provide: OAUTH_STORAGE_NAMES,
    useValue: {
      ACCESS_TOKEN: 'celero_access_token',
      ACCESS_TOKEN_STORED_AT: 'celero_access_token_stored_at',
      EXPIRES_AT: 'celero_expires_at',
      REFRESH_TOKEN: 'celero_refresh_token',
      REFRESH_STORED_AT: 'celero_refresh_token_stored_at',
      REFRESH_EXPIRES_AT: 'celero_refresh_expires_at',
      ID_TOKEN: 'celero_id_token',
      ID_TOKEN_STORED_AT: 'celero_id_token_stored_at',
      ID_TOKEN_EXPIRES_AT: 'celero_id_token_expires_at',
    },
  },
  {
    provide: FINANCIAL_RECORD_DETAILS_DOCUMENTS_CONFIGS,
    useValue: {
      pdfAssetsFolder: `${environment.cdnUrl}/assets`,
    },
  },
  {
    provide: ACCOUNT_MODAL_CONFIG_TOKEN,
    useValue: CAIXA_ACCOUNT_MODAL_CONFIG,
  },
  {
    provide: FORMAT_DATE_TICK,
    useValue: (date: string): string => {
      const [year, month] = date.split('-');
      return `${getMonthName(Number(month), true)}/${year.slice(2)}`;
    },
  },
  {
    provide: FORMAT_CASH_AND_MONEY_TOKEN,
    useValue: (value: number | string): string => {
      const money = formatMoney(value);
      const indicator = formatCash(value).trim().charAt(0);
      return `${money} ${indicator}`;
    },
  },
  {
    provide: FORMAT_BAR_TOOLTIP_VALUE_TOKEN,
    useValue: formatBarTooltipValue,
  },
  {
    provide: CASH_FLOW_MODAL_TOKEN,
    useValue: {
      checkingAccountsDisplayType: CHECKING_ACCOUNTS_DISPLAY_TYPE.ALL,
    },
  },
  {
    provide: DOWNLOAD_STATUS_CARD_CONFIG,
    useValue: {
      side: 'left'
    },
  },
  {
    provide: CHECKING_ACCOUNT_DROPDOWN_TOKEN,
    useValue: {
      humanizedAccountFormatter: (value: any) => {
        return `CC: ${value.bank_account}-${value.bank_account_digit} - Ag: ${value.bank_branch} - Nº ${value.bank_id} `;
      },
    },
  },
  {
    provide: DOWNLOAD_CONFIRM_MODAL_CONFIG,
    useValue: {
      ...DOWNLOAD_CONFIRM_MODAL_CONFIG_DEFAULT,
      limitBehavior: 'warn'
    } satisfies DownloadConfirmModalConfig
  },
  // The host sizes the iframe to its content, so a card docked to the viewport
  // would sit at the bottom of the whole page: it is mounted inside the download
  // button instead, where `download-status-card-anchor.scss` hangs it.
  {
    provide: DOWNLOAD_BUTTON_CONFIG,
    useValue: { anchorCard: true },
  },
  // Starts the download queue with the app: navigating in the host may reload
  // the iframe, and the job left running has to come back on whatever screen it
  // lands on.
  provideBfmDownloads(),
  provideCdsSnackbar(),
  ...LIST_HEADER_TOKENS,
];
